import React, { FC, Fragment, ReactNode, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import LogInAsOrganizationUserDialog from 'components/orders/log-in-as-organization-user-dialog.component';
import { formatDate, formatDateTime } from 'components/orders/order-format';
import { formatMoney, invoiceLabel } from 'components/invoices/invoice-format';
import { adminInvoicePdfUrlTemplate, adminInvoiceTemplate } from 'apollo/templates/invoices';
import { AdminInvoiceDetails, AdminInvoiceParty, AdminInvoicePdfKind, AdminInvoiceRef } from 'models/admin-invoice';
import { AdminOrderOrganization, AdminOrderUser } from 'models/admin-order';

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
    <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h3" mb={1.5}>
            {title}
        </Typography>
        {children}
    </Paper>
);

const Field: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
    <Box display="flex" gap={2} py={0.5}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
            {label}
        </Typography>
        <Typography variant="body2" component="div">
            {children ?? '–'}
        </Typography>
    </Box>
);

const PDF_LABELS: Record<AdminInvoicePdfKind, string> = {
    INVOICE: 'super-admin:INVOICE_PDF_INVOICE',
    SPECIFICATION: 'super-admin:INVOICE_PDF_SPECIFICATION',
    DELIVERY_NOTES: 'super-admin:INVOICE_PDF_DELIVERY_NOTES',
};

const userText = (user?: AdminOrderUser) => (user ? `${user.fullName ?? ''} ${user.email ?? ''}`.trim() : undefined);

const InvoiceDetails: FC = () => {
    const { t: translate } = useTranslation('super-admin');
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const client = useApolloClient();
    const [logInAs, setLogInAs] = useState<AdminOrderOrganization>();
    const [pdfError, setPdfError] = useState<string>();
    const { data, loading, error } = useQuery<{ adminInvoice: AdminInvoiceDetails | null }>(adminInvoiceTemplate, {
        variables: { id },
        fetchPolicy: 'network-only',
    });

    if (loading) return <Loader />;
    if (error) return <Alert severity="error">{error.message}</Alert>;
    const invoice = data?.adminInvoice;
    if (!invoice) return <Alert severity="warning">{translate('super-admin:INVOICE_NOT_FOUND')}</Alert>;

    // The tab is opened inside the click so popup blockers allow it; the signed URL follows.
    const openPdf = async (kind: AdminInvoicePdfKind) => {
        setPdfError(undefined);
        const tab = window.open('', '_blank');
        if (tab) tab.opener = null;
        try {
            const result = await client.query<{ adminInvoicePdfUrl: string | null }>({
                query: adminInvoicePdfUrlTemplate,
                variables: { id: invoice._id, kind },
                fetchPolicy: 'network-only',
            });
            const url = result.data?.adminInvoicePdfUrl;
            if (!url) throw new Error(translate('super-admin:INVOICE_PDF_MISSING'));
            if (tab) tab.location.href = url;
            else window.open(url, '_blank', 'noopener');
        } catch (pdfFailure) {
            tab?.close();
            setPdfError((pdfFailure as Error).message);
        }
    };

    const party = (value?: AdminInvoiceParty) =>
        value ? (
            <Box>
                <div>
                    {value.invoiceName && value.invoiceName !== value.name
                        ? `${value.name} (${value.invoiceName})`
                        : value.name}
                </div>
                <Typography variant="caption" color="text.secondary" display="block">
                    {[
                        value.number,
                        value.customerNumber &&
                            `${translate('super-admin:INVOICE_CUSTOMER_NUMBER')} ${value.customerNumber}`,
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                </Typography>
                {value.reference ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {translate('super-admin:INVOICE_REFERENCE')}: {value.reference}
                    </Typography>
                ) : undefined}
                <Typography variant="caption" color="text.secondary" display="block">
                    {[value.address, value.email].filter(Boolean).join(' · ')}
                </Typography>
            </Box>
        ) : undefined;

    const invoiceLink = (ref: AdminInvoiceRef) => (
        <Link component={RouterLink} to={`/invoices/${ref._id}`} key={ref._id} sx={{ mr: 1 }}>
            {ref.invoiceNumber ?? ref._id}
            {ref.invoiceDate ? ` (${formatDate(ref.invoiceDate)})` : ''}
        </Link>
    );

    const currency = invoice.total?.currency;

    return (
        <Fragment>
            <PageTitle
                headingStyle="h1"
                title={`${invoiceLabel(translate, 'TYPE_TITLE', invoice.type)} ${invoice.invoiceNumber ?? ''}`}
                showBackButton
                onBackArrow={() => navigate(-1)}
            />
            <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Chip label={invoiceLabel(translate, 'STATUS', invoice.status)} />
                <Chip variant="outlined" label={invoiceLabel(translate, 'TYPE', invoice.type)} />
                <Chip variant="outlined" label={invoiceLabel(translate, 'PAYMENT', invoice.paymentStatus)} />
                <Chip variant="outlined" label={invoiceLabel(translate, 'KIND', invoice.invoiceType)} />
            </Box>
            <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                    invoice.lab ? (
                        <Button
                            color="inherit"
                            size="small"
                            variant="text"
                            onClick={() => setLogInAs(invoice.lab)}
                            data-test="invoice.log-in-as"
                        >
                            {translate('super-admin:LOG_IN_AS')}
                        </Button>
                    ) : undefined
                }
            >
                {translate('super-admin:INVOICE_READ_ONLY_INFO', { name: invoice.lab?.name ?? '' })}
            </Alert>
            {invoice.processingErrorMessage ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {invoice.processingErrorMessage}
                </Alert>
            ) : undefined}

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:ORDER_PARTIES')}>
                        <Field label={translate('super-admin:INVOICE_LAB')}>{party(invoice.supplier)}</Field>
                        <Field label={translate('super-admin:INVOICE_CUSTOMER')}>
                            {party(invoice.customer)}
                            {invoice.customer?.organizationId ? (
                                <Link
                                    component={RouterLink}
                                    variant="caption"
                                    to={`/invoices?customer=${invoice.customer.organizationId}&customerName=${encodeURIComponent(
                                        invoice.customer.name ?? '',
                                    )}`}
                                >
                                    {translate('super-admin:INVOICE_CUSTOMER_INVOICES')}
                                </Link>
                            ) : undefined}
                        </Field>
                        {invoice.dentist ? (
                            <Field label={translate('super-admin:ORDER_DENTIST')}>{userText(invoice.dentist)}</Field>
                        ) : undefined}
                        <Field label={translate('super-admin:ORDER_CREATED_BY')}>{userText(invoice.createdBy)}</Field>
                    </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:INVOICE')}>
                        <Field label={translate('super-admin:INVOICE_DATE')}>{formatDate(invoice.invoiceDate)}</Field>
                        <Field label={translate('super-admin:INVOICE_DUE_DATE')}>{formatDate(invoice.dueDate)}</Field>
                        <Field label={translate('super-admin:INVOICE_PAYMENT_TERM')}>
                            {invoice.paymentTerm !== undefined && invoice.paymentTerm !== null
                                ? translate('super-admin:INVOICE_DAYS', { count: invoice.paymentTerm })
                                : undefined}
                        </Field>
                        {invoice.paidOn ? (
                            <Field label={translate('super-admin:INVOICE_PAID_ON')}>{formatDate(invoice.paidOn)}</Field>
                        ) : undefined}
                        <Field label={translate('super-admin:INVOICE_OCR')}>{invoice.ocrNumber}</Field>
                        <Field label={translate('super-admin:INVOICE_DELIVERY_METHOD')}>
                            {invoiceLabel(translate, 'DELIVERY', invoice.deliveryMethod)}
                        </Field>
                        <Field label={translate('super-admin:INVOICE_CREATED')}>
                            {formatDateTime(invoice.createdOn)}
                        </Field>
                        <Field label={translate('super-admin:INVOICE_SENT')}>{formatDateTime(invoice.sentOn)}</Field>
                        {invoice.ourReference ? (
                            <Field label={translate('super-admin:INVOICE_OUR_REFERENCE')}>{invoice.ourReference}</Field>
                        ) : undefined}
                        {invoice.costCenter ? (
                            <Field label={translate('super-admin:INVOICE_COST_CENTER')}>{invoice.costCenter}</Field>
                        ) : undefined}
                        {invoice.gln ? <Field label="GLN">{invoice.gln}</Field> : undefined}
                        {invoice.invoiceText ? (
                            <Field label={translate('super-admin:INVOICE_TEXT')}>{invoice.invoiceText}</Field>
                        ) : undefined}
                        {invoice.debitInvoice ? (
                            <Field label={translate('super-admin:INVOICE_CREDITS')}>
                                {invoiceLink(invoice.debitInvoice)}
                            </Field>
                        ) : undefined}
                        {invoice.creditInvoices.length ? (
                            <Field label={translate('super-admin:INVOICE_CREDITED_BY')}>
                                {invoice.creditInvoices.map(invoiceLink)}
                            </Field>
                        ) : undefined}
                    </Section>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:INVOICE_TOTAL')}>
                        <Field label={translate('super-admin:INVOICE_EXCL_VAT')}>
                            {formatMoney(invoice.total?.total, currency)}
                        </Field>
                        <Field label={translate('super-admin:INVOICE_VAT')}>
                            {formatMoney(invoice.total?.vat, currency)}
                        </Field>
                        <Field label={translate('super-admin:INVOICE_INCL_VAT')}>
                            <strong>{formatMoney(invoice.total?.totalIncVat, currency)}</strong>
                        </Field>
                    </Section>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Section title={translate('super-admin:INVOICE_DOCUMENTS')}>
                        {pdfError ? (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {pdfError}
                            </Alert>
                        ) : undefined}
                        {invoice.pdfs.length === 0 ? (
                            <Typography variant="body2">{translate('super-admin:INVOICE_NO_PDFS')}</Typography>
                        ) : (
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {invoice.pdfs.map((kind) => (
                                    <Button
                                        key={kind}
                                        size="small"
                                        variant="text"
                                        onClick={() => openPdf(kind)}
                                        data-test={`invoice.pdf.${kind}`}
                                    >
                                        {translate(PDF_LABELS[kind])}
                                    </Button>
                                ))}
                            </Box>
                        )}
                        <Field label={translate('super-admin:INVOICE_ORDERS')}>
                            {invoice.orders.length
                                ? invoice.orders.map((order) => (
                                      <Link
                                          component={RouterLink}
                                          to={`/orders/${order._id}`}
                                          key={order._id}
                                          sx={{ mr: 1 }}
                                      >
                                          {order.orderId ?? order._id}
                                      </Link>
                                  ))
                                : undefined}
                        </Field>
                    </Section>
                </Grid>

                <Grid item xs={12}>
                    <Section title={`${translate('super-admin:INVOICE_LINE_ITEMS')} (${invoice.lineItemCount})`}>
                        {invoice.lineItems.length === 0 ? (
                            <Typography variant="body2">{translate('super-admin:INVOICE_NO_LINE_ITEMS')}</Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('super-admin:INVOICE_LINE_DESCRIPTION')}</TableCell>
                                        <TableCell>{translate('super-admin:INVOICE_LINE_ARTICLE')}</TableCell>
                                        <TableCell align="right">
                                            {translate('super-admin:INVOICE_LINE_QUANTITY')}
                                        </TableCell>
                                        <TableCell align="right">
                                            {translate('super-admin:INVOICE_LINE_PRICE')}
                                        </TableCell>
                                        <TableCell align="right">{translate('super-admin:INVOICE_VAT')}</TableCell>
                                        <TableCell align="right">{translate('super-admin:INVOICE_INCL_VAT')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.lineItems.map((item, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {item.orderId ? (
                                                    <Link component={RouterLink} to={`/orders/${item.orderId}`}>
                                                        {item.description ?? item.orderId}
                                                    </Link>
                                                ) : (
                                                    item.description ?? '–'
                                                )}
                                            </TableCell>
                                            <TableCell>{item.articleNo ?? '–'}</TableCell>
                                            <TableCell align="right">{item.quantity ?? '–'}</TableCell>
                                            <TableCell align="right">{formatMoney(item.itemPrice)}</TableCell>
                                            <TableCell align="right">
                                                {item.itemVatPercentage !== undefined && item.itemVatPercentage !== null
                                                    ? `${item.itemVatPercentage} %`
                                                    : '–'}
                                            </TableCell>
                                            <TableCell align="right">{formatMoney(item.totalIncVat)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Section>
                </Grid>
            </Grid>

            {logInAs ? (
                <LogInAsOrganizationUserDialog
                    organization={logInAs}
                    path={`/invoices/${invoice._id}/summary`}
                    onClose={() => setLogInAs(undefined)}
                />
            ) : undefined}
        </Fragment>
    );
};

export default InvoiceDetails;
